import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, CalendarDays, Trophy, Users, Images, Handshake, Mail, Settings, LogOut, Menu, X, Loader2, Globe, Shield, FileText, Ticket } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { logout } from "@/lib/adminApi";

export const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/stages/payet", label: "Stages Payet", icon: CalendarDays },
  { to: "/admin/stages/pro", label: "Stages Pro / Semi-Pro", icon: Trophy },
  { to: "/admin/inscriptions", label: "Inscriptions", icon: Users },
  { to: "/admin/galeries", label: "Galeries", icon: Images },
  { to: "/admin/contenus", label: "Contenus", icon: FileText },
  { to: "/admin/codes-promo", label: "Codes promo", icon: Ticket },
  { to: "/admin/sponsors", label: "Sponsors / Partenaires", icon: Handshake },
  { to: "/admin/clubs", label: "Clubs partenaires", icon: Shield },
  { to: "/admin/demandes", label: "Contact / Demandes", icon: Mail },
  { to: "/admin/parametres", label: "Paramètres", icon: Settings },
];

export const StatusBadge = ({ status }) => {
  const map = {
    open: { label: "Ouvert", cls: "bg-gold/15 text-gold border-gold/40" },
    full: { label: "Complet", cls: "bg-rouge/15 text-rouge-hover border-rouge/40" },
    closed: { label: "Fermé", cls: "bg-white/10 text-white/60 border-white/20" },
    draft: { label: "Brouillon", cls: "bg-white/5 text-white/40 border-white/10" },
    archived: { label: "Archivé", cls: "bg-white/5 text-white/35 border-white/10" },
  };
  const s = map[status] || map.draft;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-[0.15em] border px-2.5 py-1 ${s.cls}`}>
      {s.label}
    </span>
  );
};

const SiteLink = ({ testId, onClick }) => (
  <Link
    to="/"
    data-testid={testId}
    onClick={onClick}
    className="flex items-center justify-center gap-2 px-4 py-2.5 border border-gold/40 text-gold text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-gold hover:text-noir transition-colors"
  >
    <Globe size={14} /> Voir le site
  </Link>
);

const AdminLayout = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("staff_token")) {
      navigate("/admin/login");
      return;
    }
    adminApi
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => navigate("/admin/login"))
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-noir flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-gold" />
      </div>
    );
  }

  const nav = (
    <nav className="flex flex-col gap-1">
      {ADMIN_NAV.map(({ to, label, icon: Icon, end }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          onClick={() => setMenuOpen(false)}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-3 text-sm font-semibold transition-colors ${
              isActive ? "bg-gold/10 text-gold border-l-2 border-gold" : "text-white/60 hover:text-white hover:bg-white/5 border-l-2 border-transparent"
            }`
          }
        >
          <Icon size={17} /> {label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div data-testid="admin-layout" className="min-h-screen bg-noir text-white flex">
      {/* Sidebar desktop */}
      <aside className="hidden lg:flex flex-col w-64 shrink-0 border-r border-noir-border bg-noir-surface min-h-screen sticky top-0">
        <div className="p-6 border-b border-noir-border">
          <Link to="/admin" className="font-display text-2xl tracking-wide">
            <span className="text-gold">3M</span> MATCH PRO
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold/70 mt-1">Staff</p>
        </div>
        <div className="flex-1 py-4 overflow-y-auto">{nav}</div>
        <div className="p-4 border-t border-noir-border">
          <SiteLink testId="admin-back-to-site-sidebar" />
        </div>
      </aside>

      {/* Mobile drawer */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-noir/98 backdrop-blur-xl flex flex-col">
          <div className="flex items-center justify-between p-5 border-b border-noir-border">
            <span className="font-display text-2xl tracking-wide"><span className="text-gold">3M</span> STAFF</span>
            <button data-testid="admin-mobile-close" onClick={() => setMenuOpen(false)} className="w-10 h-10 flex items-center justify-center border border-white/15">
              <X size={20} />
            </button>
          </div>
          <div className="py-4 overflow-y-auto flex-1">{nav}</div>
          <div className="p-5 border-t border-noir-border">
            <SiteLink testId="admin-back-to-site-mobile" onClick={() => setMenuOpen(false)} />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 bg-noir/90 backdrop-blur-xl border-b border-noir-border px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button data-testid="admin-mobile-menu" onClick={() => setMenuOpen(true)} className="lg:hidden w-10 h-10 flex items-center justify-center border border-white/15">
              <Menu size={18} />
            </button>
            <span className="font-display text-xl tracking-wide hidden sm:block">
              <span className="text-gold">3M MATCH PRO</span> — STAFF
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="admin-user-email" className="text-xs text-white/50 hidden md:block">{user?.email}</span>
            <Link
              to="/"
              data-testid="admin-back-to-site"
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-gold border border-gold/40 hover:bg-gold hover:text-noir px-4 py-2 rounded-full transition-colors"
            >
              <Globe size={14} /> <span className="hidden sm:inline">Voir le site</span>
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-white/60 hover:text-rouge-hover border border-white/15 hover:border-rouge/50 px-4 py-2 transition-colors"
            >
              <LogOut size={14} /> <span className="hidden sm:inline">Déconnexion</span>
            </button>
          </div>
        </header>
        <main className="flex-1 p-5 md:p-8 max-w-7xl w-full mx-auto">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
